/**
 * @lodex-pro/web-admin — small pure helpers for the Billing pages (Task 21.7a). Kept out of
 * the components so the tests can pin them directly.
 */
import { ApiError } from '@lodex-pro/web-shared';
import type { BillingStatus, CustomerRow, SubscriptionSummary } from './billing-hooks';

export const BILLING_STATUS_LABELS: Readonly<Record<BillingStatus, string>> = {
  ACTIVE: 'Billed',
  NOT_BILLED: 'Not billed',
  PAUSED: 'Paused',
};

/** Basis points → "8.25%" (two decimals at most, no trailing zeros). */
export function bpsToPercent(bps: number): string {
  return `${String(Number((bps / 100).toFixed(2)))}%`;
}

/**
 * "8.25" or "8.25%" → 825 basis points; null when it is not a percentage between 0 and 100
 * with at most two decimals.
 */
export function percentToBps(input: string): number | null {
  const t = input.trim().replace(/%$/, '').trim();
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(t)) return null;
  const [whole, frac = ''] = t.split('.');
  const bps = Number(whole) * 100 + Number(frac.padEnd(2, '0'));
  return bps >= 0 && bps <= 10_000 ? bps : null;
}

/** Comma, space or newline separated addresses → trimmed, lowercased, de-duplicated. */
export function parseEmailList(input: string): string[] {
  const seen = new Set<string>();
  for (const part of input.split(/[\s,;]+/)) {
    const v = part.trim().toLowerCase();
    if (v !== '') seen.add(v);
  }
  return [...seen];
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** The first address in the list that is not an email address, or null. */
export function firstBadEmail(list: readonly string[]): string | null {
  return list.find((e) => !EMAIL_RE.test(e)) ?? null;
}

/** A subscription as one line: "Hauler Professional, since 2026-11-01" (or "…, 2026-11-01 to 2027-01-01"). */
export function subscriptionLine(s: SubscriptionSummary | null): string {
  if (s === null) return 'No billing plan';
  const name = s.rateCardName ?? 'Unnamed rate card';
  return s.endsOn === null
    ? `${name}, since ${s.startsOn}`
    : `${name}, ${s.startsOn} to ${s.endsOn}`;
}

/**
 * DEC-366 Q12 in v1 (Session 6, 2026-10-19): the only warning is a customer switched to billed
 * with no billing plan, which would invoice nothing. A rate card has no link to a feature
 * template yet (that column comes with migration 0232), so no plan-vs-template guess is made.
 */
export type PlanGap = 'NO_PLAN' | 'ENDS_THIS_PERIOD';

export const PLAN_GAP_LABELS: Readonly<Record<PlanGap, string>> = {
  NO_PLAN: 'No billing plan',
  ENDS_THIS_PERIOD: 'Plan ends this month',
};

/** The last day of today's month (YYYY-MM-DD): the end of the billing period `today` is in. */
export function periodEndOf(today: string): string {
  const y = Number(today.slice(0, 4));
  const mo = Number(today.slice(5, 7));
  // Day 0 of the next month is the last day of this one (the engine's monthPeriod does the same).
  const last = new Date(Date.UTC(y, mo, 0)).getUTCDate();
  return `${today.slice(0, 7)}-${String(last).padStart(2, '0')}`;
}

/**
 * Why a billed customer's invoice for the current month would come out empty, or null.
 * The invoice engine bills the plan in force on the period's LAST day (ends_on >= periodEnd,
 * invoice-generator.ts), so a plan ending mid-month bills nothing for that month either
 * (#945 review MED-1), as does no plan at all or one that already ended (#940 LOW-2).
 * `today` is in the billing time zone; YYYY-MM-DD strings compare as dates.
 */
export function billedWithoutPlan(
  c: Pick<CustomerRow, 'billingStatus' | 'latestSubscription'>,
  today: string,
): PlanGap | null {
  if (c.billingStatus !== 'ACTIVE') return null;
  const s = c.latestSubscription;
  if (s === null || (s.endsOn !== null && s.endsOn < today)) return 'NO_PLAN';
  if (s.endsOn !== null && s.endsOn < periodEndOf(today)) return 'ENDS_THIS_PERIOD';
  return null;
}

/** Today as YYYY-MM-DD in the given zone (the billing timezone decides which day it is). */
export function todayIn(timeZone: string, now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
  return parts;
}

/**
 * An API refusal in words: its field reasons when the server named them, else the caller's
 * sentence with the error code. Never ApiError.message, which is "API 409: /v1/...".
 */
/** The API's field reasons in words (#949 review LOW-3); an unknown one reads as "is not valid". */
const FIELD_REASONS: Readonly<Record<string, string>> = {
  sub_cent_precision: 'must be whole cents',
  blank: 'is required',
  negative: 'cannot be negative',
  zero: 'cannot be $0',
  invalid: 'is not valid',
  before_starts_on: 'must be on or after the start date',
  unknown_time_zone: 'is not a known time zone',
  no_matching_band: 'falls in no band',
  invalid_type: 'is missing or the wrong kind of value',
  invalid_string: 'is not valid',
  invalid_enum_value: 'is not one of the allowed values',
  too_small: 'is too small or too short',
  too_big: 'is too large or too long',
  custom: 'is not valid',
};

const FIELD_NAMES: Readonly<Record<string, string>> = {
  fromQty: 'from',
  toQty: 'to',
  unitPrice: 'unit price',
  includedQuantity: 'included quantity',
  effectiveFrom: 'effective date',
  startsOn: 'start date',
  endsOn: 'end date',
  toEmails: 'To addresses',
  ccEmails: 'CC addresses',
  logoDataUri: 'logo',
};

/** "body.lines.0.bands.1.fromQty" → "Line 1, band 2, from". */
export function fieldPathText(path: string): string {
  const parts = path.split('.').filter((p) => p !== 'body' && p !== 'params' && p !== 'query');
  const out: string[] = [];
  for (let i = 0; i < parts.length; i += 1) {
    const p = parts[i] ?? '';
    const next = parts[i + 1];
    if (next !== undefined && /^\d+$/.test(next)) {
      const label = p.endsWith('s') ? p.slice(0, -1) : p;
      const named = out.length === 0 ? label.charAt(0).toUpperCase() + label.slice(1) : label;
      out.push(`${named} ${String(Number(next) + 1)}`);
      i += 1;
    } else {
      out.push(FIELD_NAMES[p] ?? p.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase());
    }
  }
  return out.join(', ');
}

export function billingErrorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.fields.length > 0) {
      return err.fields
        .map((f) => `${fieldPathText(f.path)} ${FIELD_REASONS[f.reason] ?? 'is not valid'}`)
        .join('; ');
    }
    if (err.code !== null) return `${fallback} (${err.code})`;
  }
  return fallback;
}

/**
 * 21.7d (DEC-366 Q12, with 0232's rate_cards.feature_template): a hauler billed on a card that
 * sells one feature plan while its features run on another. Only when the card names a plan;
 * an unnamed card is not compared. Returns the two names, or null when they agree.
 */
export function planMismatch(
  cardTemplate: string | null | undefined,
  haulerTemplate: string | null | undefined,
): { card: string; hauler: string } | null {
  if (cardTemplate === null || cardTemplate === undefined) return null;
  if (haulerTemplate === null || haulerTemplate === undefined) return null;
  return cardTemplate === haulerTemplate ? null : { card: cardTemplate, hauler: haulerTemplate };
}

/** A hauler feature plan in words: "PROFESSIONAL" → "Professional" (#974 review LOW-5). */
export function templateLabel(template: string): string {
  return template.charAt(0) + template.slice(1).toLowerCase();
}

// ── Plan actions, said before the click (2026-10-21: a plan was ended 7 s after it was set,
// because the screen did not say what Change and End would do) ──────────────────────────

/** `iso` (YYYY-MM-DD) moved by `days`, as a calendar date (no time zone involved). */
export function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** A plan's dates, as the history and the confirms read them. */
export interface PlanDates {
  readonly id: string;
  readonly rateCardId: string | null;
  readonly rateCardName: string | null;
  readonly startsOn: string;
  readonly endsOn: string | null;
  readonly addOnCodes: readonly string[];
}

const cardName = (s: Pick<PlanDates, 'rateCardName'>): string =>
  s.rateCardName ?? 'Unnamed rate card';

/** The plan in force on `today`, or null. */
export function planInForce<T extends PlanDates>(subs: readonly T[], today: string): T | null {
  return subs.find((s) => s.startsOn <= today && (s.endsOn === null || s.endsOn >= today)) ?? null;
}

/** The next plan to start after `today`, or null. */
export function nextPlan<T extends PlanDates>(subs: readonly T[], today: string): T | null {
  const later = subs.filter((s) => s.startsOn > today);
  return later.reduce<T | null>((a, s) => (a === null || s.startsOn < a.startsOn ? s : a), null);
}

const sameCodes = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && [...a].sort().join(',') === [...b].sort().join(',');

/**
 * What "Set billing plan" will do, in words, or why it can't. BLOCK names the problem and the
 * button stays off; CONFIRM is the sentence the confirm step shows. Mirrors the API: an open
 * plan that started earlier is closed the day before the new one starts; any other overlap is
 * refused.
 */
export function changePlanPreview(input: {
  readonly subs: readonly PlanDates[];
  readonly rateCardId: string;
  readonly rateCardName: string;
  readonly startsOn: string;
  readonly addOnCodes: readonly string[];
}): { readonly kind: 'BLOCK' | 'CONFIRM'; readonly text: string } {
  const { subs, rateCardId, rateCardName, startsOn, addOnCodes } = input;
  const open = subs.find((s) => s.endsOn === null) ?? null;
  if (open !== null && open.rateCardId === rateCardId && sameCodes(open.addOnCodes, addOnCodes)) {
    return {
      kind: 'BLOCK',
      text: `This is the plan they're already on: ${cardName(open)}, since ${open.startsOn}. Nothing would change.`,
    };
  }
  if (open !== null && startsOn <= open.startsOn) {
    return {
      kind: 'BLOCK',
      text: `${cardName(open)} starts ${open.startsOn}, so a new plan must start after that. Pick a later date, or end ${cardName(open)} first.`,
    };
  }
  const clash = subs.find(
    (s) => s.endsOn !== null && (s.endsOn >= startsOn || s.startsOn >= startsOn),
  );
  if (clash !== undefined) {
    return {
      kind: 'BLOCK',
      text: `That overlaps ${cardName(clash)} (${clash.startsOn} to ${clash.endsOn ?? ''}). Pick a start date after ${clash.endsOn ?? ''}.`,
    };
  }
  const codes =
    open !== null && open.rateCardId === rateCardId
      ? ` Same rate card; add-ons change from ${open.addOnCodes.join(', ') || 'none'} to ${addOnCodes.join(', ') || 'none'}.`
      : '';
  if (open === null) {
    return { kind: 'CONFIRM', text: `This starts ${rateCardName} on ${startsOn}.` };
  }
  return {
    kind: 'CONFIRM',
    text: `This ends ${cardName(open)} on ${addDays(startsOn, -1)} and starts ${rateCardName} on ${startsOn}.${codes}`,
  };
}

/**
 * What "End plan" will do, in words. Invoices bill the plan in force on the period's last day
 * (billedWithoutPlan), so a plan ending before its month's last day leaves that month unbilled
 * too, and the sentence says so.
 */
export function endPlanPreview(input: {
  readonly sub: Pick<PlanDates, 'rateCardName'>;
  readonly customerName: string;
  readonly endsOn: string;
}): string {
  const { sub, customerName, endsOn } = input;
  const from = addDays(endsOn, 1);
  const monthEnd = periodEndOf(endsOn);
  const month =
    endsOn < monthEnd
      ? ` Invoices bill the plan in force on a month's last day, so ${endsOn.slice(0, 7)} won't be invoiced either.`
      : '';
  return `${cardName(sub)} ends after ${endsOn}. From ${from}, ${customerName} will have NO billing plan and won't be invoiced.${month}`;
}

/** How long after a plan is ended the end can be undone (the API's SUBSCRIPTION_UNDO_END_MINUTES). */
export const UNDO_END_MINUTES = 10;

/**
 * Whether to offer "Undo end" on a plan. The API decides (its latest audit row is an End from
 * the last UNDO_END_MINUTES, and no later plan exists) and returns the deadline as
 * `undoEndUntil`; this only compares it with the clock, so the button disappears when the
 * window closes (#1014 review MED-1 / LOW-2: updatedAt alone offered it on a plan created
 * already closed).
 */
export function canUndoEnd(
  sub: { readonly endsOn: string | null; readonly undoEndUntil?: string | null },
  now: Date = new Date(),
): boolean {
  if (sub.endsOn === null || sub.undoEndUntil === null || sub.undoEndUntil === undefined) {
    return false;
  }
  return now.getTime() < new Date(sub.undoEndUntil).getTime();
}
